import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import type {Finding} from './types'

/** Display order for severity levels (most severe first) */
export const SEVERITY_ORDER: RuleSeverity[] = [
  'failure',
  'critical',
  'high',
  'error',
  'medium',
  'warning',
  'low',
  'note',
  'nit',
  'notice',
  'none',
]

const getSeveritySortIndex = (severity: RuleSeverity | undefined): number => {
  if (!severity) {
    return SEVERITY_ORDER.length
  }

  const index = SEVERITY_ORDER.indexOf(severity)
  return index === -1 ? SEVERITY_ORDER.length : index
}

const SEVERITY_MAP: Record<string, RuleSeverity> = {
  critical: 'critical',
  high: 'high',
  moderate: 'medium',
  low: 'low',
}

const SOURCE_LABELS: Record<string, string> = {
  check_suite: 'Actions',
  pull_request_backfill: 'Actions',
  check_run: 'Actions',
  pr_review_comment: 'Review comment',
  pr_timeline_comment: 'Pull request comment',
  ccr_review_comment: 'Copilot',
  ci_analysis: 'CI Analysis',
}

const DISPLAY_PATH_SEGMENTS = 3

export const getFindingSeverity = (finding: Finding): RuleSeverity | undefined => {
  return SEVERITY_MAP[finding.severity]
}

const getSourceLabel = (source: string): string => SOURCE_LABELS[source] ?? source

export const getFindingSourceLabels = (finding: Finding): string[] => {
  const labels: string[] = []
  const seen = new Set<string>()

  for (const reference of finding.sourceReferences ?? []) {
    const label = getSourceLabel(reference.source)
    if (seen.has(label)) continue
    seen.add(label)
    labels.push(label)
  }

  return labels
}

export interface SourceAuthor {
  login: string
  avatarUrl?: string
}

export interface FindingSource {
  key: string
  label: string
  url?: string
  author?: SourceAuthor
}

export const getFindingSources = (finding: Finding): FindingSource[] => {
  const sources: FindingSource[] = []
  const seenKeys = new Set<string>()

  for (const reference of finding.sourceReferences ?? []) {
    const login = reference.authorLogin?.trim() || undefined
    const avatarUrl = reference.authorAvatarUrl || undefined
    const author = login ? {login, avatarUrl} : undefined

    if (!reference.url && !author) continue

    const label = getSourceLabel(reference.source)
    const key = `${label}|${login?.toLowerCase() ?? ''}`
    if (seenKeys.has(key)) continue
    seenKeys.add(key)

    sources.push({
      key,
      label,
      url: reference.url,
      author,
    })
  }

  return sources
}

export const getFindingSourceAuthors = (finding: Finding): SourceAuthor[] => {
  const authors: SourceAuthor[] = []
  const seenLogins = new Set<string>()

  for (const reference of finding.sourceReferences ?? []) {
    const login = reference.authorLogin?.trim()
    if (!login) continue

    const key = login.toLowerCase()
    if (seenLogins.has(key)) continue

    seenLogins.add(key)
    authors.push({login, avatarUrl: reference.authorAvatarUrl || undefined})
  }

  return authors
}

export const getDisplayFilePath = (filePath: string): string => {
  const segments = filePath.replace(/\\/g, '/').split('/').filter(Boolean)
  if (segments.length <= DISPLAY_PATH_SEGMENTS) return filePath

  return `.../${segments.slice(-DISPLAY_PATH_SEGMENTS).join('/')}`
}

const getFindingLocationSuffix = (finding: Finding): string => {
  if (finding.startLine === finding.endLine) {
    let suffix = `:${finding.startLine}`
    if (finding.startColumn > 0) {
      suffix += `:${finding.startColumn}`
    }
    return suffix
  }

  return `:${finding.startLine}-${finding.endLine}`
}

export const getFindingFullFileLocation = (finding: Finding): string | null => {
  if (!finding.filePath) return null

  return `${finding.filePath}${getFindingLocationSuffix(finding)}`
}

export const getFindingFileLocation = (finding: Finding): string | null => {
  if (!finding.filePath) return null

  return `${getDisplayFilePath(finding.filePath)}${getFindingLocationSuffix(finding)}`
}

export const getFindingTitle = (finding: Finding): string => {
  const msg = finding.groupTitle
  if (msg.length <= 100) return msg
  const truncated = msg.slice(0, 100)
  const lastSpace = truncated.lastIndexOf(' ')
  return lastSpace > 0 ? `${truncated.slice(0, lastSpace)}…` : `${truncated}…`
}

export const sortFindingsBySeverity = (findings: Finding[]): Finding[] => {
  return [...findings].sort((a, b) => {
    const indexA = getSeveritySortIndex(getFindingSeverity(a))
    const indexB = getSeveritySortIndex(getFindingSeverity(b))
    return indexA - indexB
  })
}

const HIGH_ATTENTION_SEVERITIES: ReadonlySet<RuleSeverity> = new Set(['critical', 'high'])

export const isNeedsAttention = (finding: Finding): boolean => {
  const severity = getFindingSeverity(finding)
  return !!severity && HIGH_ATTENTION_SEVERITIES.has(severity)
}
